import { useEffect } from "react";
import { useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { Button, Card, LinkButton } from "@/components/ui";
import { ApiError } from "@/lib/api";
import { useT } from "@/lib/session";
import { go } from "@/lib/nav";

/** Skeleton shown while a screen loads (same as the old loading.tsx). */
export function PendingScreen() {
  return (
    <div className="animate-pulse space-y-4 pt-2" aria-busy="true">
      <div className="h-7 w-1/2 rounded-lg bg-surface-2" />
      <div className="h-24 rounded-2xl bg-surface" />
      <div className="h-24 rounded-2xl bg-surface" />
      <div className="h-24 rounded-2xl bg-surface" />
    </div>
  );
}

export function NotFound() {
  const t = useT();
  return (
    <main className="mx-auto max-w-sm px-6 pt-24">
      <Card className="space-y-4 text-center">
        <p className="text-muted">{t.pageNotFound}</p>
        <LinkButton href="/" variant="secondary" className="w-full">
          {t.goHome}
        </LinkButton>
      </Card>
    </main>
  );
}

export function RouteError({ error, reset }: ErrorComponentProps) {
  const t = useT();
  const router = useRouter();
  if (error instanceof ApiError && (error.status === 404 || error.status === 403)) return <NotFound />;
  const offline = typeof navigator !== "undefined" && !navigator.onLine;
  return (
    <main className="mx-auto max-w-sm px-6 pt-24">
      <Card className="space-y-4 text-center">
        {offline ? (
          <>
            <p className="font-semibold">{t.offlineTitle}</p>
            <p className="text-sm text-muted">{t.offlineBody}</p>
          </>
        ) : (
          <p className="text-muted">{t.loadFailed}</p>
        )}
        <Button
          className="w-full"
          onClick={() => {
            reset();
            router.invalidate();
          }}
        >
          {t.retry}
        </Button>
      </Card>
    </main>
  );
}

/** Replaces the current screen with another app path (what redirect() did in a server page). */
export function Redirect({ to }: { to: string }) {
  useEffect(() => {
    void go(to, { replace: true });
  }, [to]);
  return null;
}
